<script setup>
  import GlobalEmitter from '~/glxp/utils/emitter'
  import { useLoaderStore } from '~/store/loader'
  import { useContentful } from '~/composables/useContentful'
  import query from '~/graphql/page-content'

  const contentful = useContentful()
  const route = useRoute()
  const slug = computed(() => route.params.slug)

  /**
   * Data fetching
   */
  const router = useRouter()
  if(route.params.slug == 'upcoming-experiences-test'){
    router.push('/upcoming-experiences-live')
  }

  const { data, error } = await useAsyncData(async () => {
    const response = await contentful.graphql(query, {
      slug: slug.value,
      sectionsSkip: 0,
    })
    const json = await response.json()
    console.info('page collection: ', json);
    const { items, total } = json.data.modularPageCollection

    if (total < 1) {
      throw new Error('Page not found');
    }

    const page = items[0]
    const { total: totalSections, items: sections } = page.sectionsCollection

    // Contentful has complexity limits. If there are more than 12 sections, we
    // have to fetch them in multiple requests.
    if (totalSections > sections.length) {
      const sectionsPerPage = sections.length
      const nPages = Math.ceil(totalSections / sectionsPerPage)
      const responses = await Promise.all(
        [...new Array(nPages - 1)].map(async (_, i) => {
          const response = await contentful.graphql(query, {
            slug: slug.value,
            sectionsSkip: sectionsPerPage * (i + 1),
          })
          const json = await response.json()
          return {
            index: i,
            items:
              json.data.modularPageCollection.items[0]?.sectionsCollection?.items,
          }
        })
      )

      // sort responses by index to eliminate the possiblity of race conditions
      responses.sort((a, b) => (a.index > b.index ? 1 : -1))

      // add them to the page data
      responses.forEach((r) => {
        page.sectionsCollection.items = [
          ...page.sectionsCollection.items,
          ...r.items,
        ]
      })
    }

    return page
  })

  watchEffect(() => {
    if (error.value) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page not found',
        fatal: true,
      })
    }
  })

  useHead({
    title: data.value?.title,
    titleTemplate: (title) => title && `${title} | Virgin Galactic`,
    meta: data.value?.seo && [
      { name: 'title', content: () => data.value.seo.title },
      { name: 'description', content: () => data.value.seo.description },
      data.value.seo.keywords && {
        name: 'keywords',
        content: () => data.value.seo.keywords.join(', '),
      },
      (data.value.seo.noindex || data.value.seo.nofollow) && {
        name: 'robots',
        content: () =>
          [
            data.value?.seo?.noindex ? 'noindex' : null,
            data.value?.seo?.nofollow ? 'nofollow' : null,
          ]
            .filter((value) => value !== null)
            .join(','),
      },
    ],
  })

  /**
   * Component imports
   */

  const ModuleContentCarousel = resolveComponent('ModuleContentCarousel')
  const ModuleContentGrid = resolveComponent('ModuleContentGrid')
  const ModuleFaq = resolveComponent('ModuleFAQ')
  const ModuleImageGallery = resolveComponent('ModuleImageGallery')
  const ModuleQuote = resolveComponent('ModuleQuote')
  const ModuleText = resolveComponent('ModuleText')
  const ModuleVideo = resolveComponent('ModuleVideo')
  const ModuleCallToActionBanner = resolveComponent('CallToActionBanner')
  const ModuleStripePayment = resolveComponent('StripeElements')
  const ModuleSalesforceSignUp = resolveComponent('SalesforceSignUp')
  const ModuleContentVerticalGrid = resolveComponent('ModuleContentVerticalGrid')
  const ModuleImage = resolveComponent('ModuleImage')


  const components = {
    ModuleContentCarousel,
    ModuleContentGrid,
    ModuleFaq,
    ModuleImageGallery,
    ModuleQuote,
    ModuleText,
    ModuleVideo,
    ModuleCallToActionBanner,
    ModuleStripePayment,
    ModuleSalesforceSignUp,
    ModuleContentVerticalGrid,
    ModuleImage
  }

  /**
   * Sections with dynamic components
   */
  const sections = computed(() =>
    data.value.sectionsCollection?.items.map((item) => {
      const output = {
        component: components[item.__typename],
        ...item,
      }

      if (['ModuleFaq', 'ModuleContentGrid', 'ModuleContentVerticalGrid'].indexOf(item.__typename) > -1) {
        output.items = item.itemsCollection.items
      }

      return output
    })
  )

  /**
   * Anchors
   */
  const el = ref(null)
  const anchors = computed(() =>
    data.value.sectionsCollection?.items.filter(
      (item) => item.__typename === 'ModuleNavigationAnchor'
    )
  )
  const hasAnchors = computed(() => !!anchors.value?.length)

  function scrollToAnchor(slug) {
    const scrollElement = el.value.querySelector(`#${slug}`)
    if (!scrollElement) {
      return
    }

    const bounds = scrollElement.getBoundingClientRect()
    const { top } = bounds
    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop

    window.scrollTo({
      top: top + scrollTop - 50,
      behavior: 'smooth',
    })
  }

  /**
   * Lifecycle
   */
  function openOsanoWidget() {
    Osano.cm.showDrawer('osano-cm-dom-info-dialog-open')
  }
  const loaderState = useLoaderStore()
  onMounted(() => {
    GlobalEmitter.emit('webgl_raf_stop')
    GlobalEmitter.emit('webgl_loading_progress', { progress: 100 })
    loaderState.isOpen = false
    const manageCookieSettingsLinkElement = document.querySelector(
      '#manage-cookie-settings a'
    )

    if (manageCookieSettingsLinkElement) {
      manageCookieSettingsLinkElement.addEventListener('click', openOsanoWidget)
    }
    const manageCookieSettingsLinkUseElement = document.querySelector(
      '#manage-cookie-settings-use a'
    )

    if (manageCookieSettingsLinkUseElement) {
      manageCookieSettingsLinkUseElement.addEventListener('click', openOsanoWidget)
    }

    const manageThirdCookieSettingsLinkElement = document.querySelector(
      '#manage-third-cookie-settings a'
    )

    if (manageThirdCookieSettingsLinkElement) {
      manageThirdCookieSettingsLinkElement.addEventListener('click', openOsanoWidget)
    }
  })
  onBeforeUnmount(() => {
    const manageCookieSettingsLinkElement = document.querySelector(
      '#manage-cookie-settings a'
    )

    if (manageCookieSettingsLinkElement) {

      manageCookieSettingsLinkElement.removeEventListener(
        'click',
        openOsanoWidget
      )
    }
    const manageCookieSettingsLinkUseElement = document.querySelector(
      '#manage-cookie-settings-use a'
    )

    if (manageCookieSettingsLinkUseElement) {

      manageCookieSettingsLinkUseElement.removeEventListener(
        'click',
        openOsanoWidget
      )
    }
    const manageThirdCookieSettingsLinkElement = document.querySelector(
      '#manage-third-cookie-settings a'
    )

    if (manageThirdCookieSettingsLinkElement) {

      manageThirdCookieSettingsLinkElement.removeEventListener(
        'click',
        openOsanoWidget
      )
    }
  })
</script>

<template>
  <article v-if="data" class="page-content container container-1400" :class="[
      { 'page-content--has-anchors': hasAnchors },
      { '__no-hero': true },
      `page--${slug}`,
      
    ]" ref="el">
   <!---- <div  style="width: 25%;"></div>
    <div style="width: 75%;"> -->
    <astronautLayout :enableSidePanel="data.enableSidePanel" :data="data">
    <PageHero class="page-content__hero" v-if="data.headline || data.eyebrow || data.image" :title="data.headline"
      :eyebrow="data.eyebrow" :copy="data.copy" :image="data.image" :wide-text="true" :enableSidePanel="data.enableSidePanel">
      <!--<ButtonPill
        v-if="slug === 'nxtgen-sign-up'"
        class="page-content__hero-button"
        outline
        icon="chevron-right"
        animate
        href="/nxtgen"
        >Step forward</ButtonPill
      >-->
    </PageHero>

    <ModularPageNavigation v-if="data.useAnchorNav && anchors?.length" :title="data.title" :anchors="anchors"
      @scroll-to-anchor="scrollToAnchor" />

    <div class="page-content__sections">
      <template v-for="section in sections">
        <div v-if="section.__typename === 'ModuleNavigationAnchor'" :id="section.slug" />
        <component v-else class="page-content__section" :key="section.sys.id" :is="section.component"
          v-bind="section" :addModuleText="data.brochurePage ? false :true "/>
        <hr v-if="data.brochurePage">
      </template>
    </div>
  </astronautLayout>
  </article>
</template>

<style lang="scss">
  // Utils
  @import 'styles/utils/variables';

  // Vendors
  @import 'styles/vendors/sass-mq/mq';
  @import 'styles/base/sidebar';

  .page-content {
    hr {
		height: 1px;
		background: linear-gradient(269.91deg, #7019F8 0.06%, #C80AFC 101.13%);
		margin: -12px -12px;
    border: none
	}
    background: url('~/assets/images/home/bg-stars-repeat.jpg') center top repeat;

    // glow
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      z-index: 0;

      height: 1300px;

      background: radial-gradient(62.22% 94.81% at 99.13% 0%,
          rgba(200, 10, 251, 0.15) 0%,
          rgba(200, 10, 251, 0) 100%),
        radial-gradient(50.05% 50.05% at 50% 0%,
          rgba(21, 42, 244, 0.22) 0%,
          rgba(21, 42, 244, 0) 100%),
        linear-gradient(to top, rgba(13, 6, 29, 0) 0%, #0d061d 100%);
      transform: matrix(-1, 0, 0, 1, 0, 0);
    }

    padding-bottom: 125px;

    &__hero-button {
      margin-top: 32px;
    }

    &__hero,
    &__sections {
      position: relative;
      z-index: 1;
    }

    &__sections {
      // margin-bottom: 100px;
    }

    &__section {
      &:not(:first-of-type) {
        margin-top: 72px;
      }

      margin-top: 50px;
      margin-bottom: 50px;

      @include mq($from: 'l') using ($from) {
        margin-top: 125px;
        margin-bottom: 125px;
      }
    }

    &.__no-hero {
      padding-top: 70px;
      min-height: calc(100vh - 490px);

      @include mq($from: 'l') using ($from) {
        padding-top: 50px;
      }
    }
  }
  .article-flex {
    display: flex;
    flex-direction: row;
  }

  // individual pages
  .page--nxtgen-sign-up {
    max-width: unset;
    margin: 0;
    padding: 0 0 150px !important;
    background: #000;

    .page-hero {
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1)),
        url('~/assets/images/nxtgen/hero.png') center no-repeat;
      background-size: cover;
      padding-top: 150px;

      .section-header::before {
        content: '';
        background: url('~/assets/images/nxtgen/nxtgen-logo.svg') top center no-repeat;
        width: 175px;
        height: 175px;

        @include mq($from: 'l') using ($from) {
          width: 225px;
          height: 225px;
        }

        @include mq($from: 'xl') using ($from) {
          margin-bottom: 75px;
        }
      }
    }

    .rich-text-image {
      margin: 0 -15px 32px;
      width: calc(100% + 30px);
      max-width: unset;

      @include mq($from: 'l') using ($from) {
        width: calc(100% + 80px);
        margin: 0 -40px 32px;
      }
    }

    .page-content__section {
      @include mq($until: 'xl') using ($until) {
        padding-left: 15px;
        padding-right: 15px;
      }

      .text__headline {
        text-align: center;
      }
    }

    .page-content__section:nth-child(3) {
      .module-text__cta {
        display: flex;
        justify-content: center;
      }
    }
  }

  .page--fpo-test-3,
  .page--NXTGEN-Intro {
    .page-content__sections {
      .module-video {
        margin-bottom: 25px;
      }

      .module-text {
        margin-top: 0;
      }

      @include mq($from: 'l') using ($from) {
        .module-video {
          margin-bottom: 45px;
        }
      }

      .module-text__cta {
        display: flex;
        justify-content: center;
      }
    }
  }
</style>